import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, articles } from '@/mocks/mangaData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || '');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      setCurrentPage(1);
    }
  }, [slug]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeCategory),
    [activeCategory]
  );

  const filteredArticles = useMemo(
    () => articles.filter((a) => a.categorySlug === activeCategory),
    [activeCategory]
  );

  const totalPages = Math.ceil(filteredArticles.length / itemsPerPage);
  const paginatedArticles = filteredArticles.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    document.title = `${currentCategory?.name || '分类'} - 禁漫天漫画分类导航`;
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && currentCategory) {
      metaDescription.setAttribute(
        'content',
        `禁漫天${currentCategory.name}漫画分类，${currentCategory.description}。浏览精选${currentCategory.name}漫画指南和推荐文章。`
      );
    }
  }, [currentCategory]);

  if (!currentCategory) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
            <i className="ri-emotion-sad-line text-2xl"></i>
          </div>
          <p className="text-foreground-600">未找到该分类</p>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 mt-2 inline-block">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {/* Header */}
      <div className="pt-20 bg-background-100 border-b border-background-200/60">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: '分类导航', path: '/category/rexue-maoxian' },
            { label: currentCategory.name },
          ]}
        />

        <div className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                <i className={`${currentCategory.icon} text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">
                  {currentCategory.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Category tabs */}
      <div className="w-full px-4 md:px-6 py-5 border-b border-background-200/60 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 overflow-x-auto pb-1 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${cat.icon} text-xs`}></i>
                </div>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Articles */}
      <div className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-7xl mx-auto">
          {filteredArticles.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-file-list-3-line text-2xl"></i>
              </div>
              <p className="text-foreground-600">该分类暂无文章</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {paginatedArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative w-full h-40 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2 leading-snug">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-600 line-clamp-2 leading-relaxed mb-3">
                        {article.description}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <div className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-eye-line text-xs"></i>
                          </div>
                          {article.views.toLocaleString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <div className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-star-fill text-xs text-accent-500"></i>
                          </div>
                          {article.rating}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200/70 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-all duration-200 ${
                        currentPage === page
                          ? 'bg-primary-500 text-white'
                          : 'text-foreground-600 hover:bg-background-100 border border-background-200/70'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200/70 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}